import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Button,
  Card,
  Container,
  Grid,
  Step,
  StepLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
import axios from 'axios';
import { CustomStepper } from 'components/CustomStepper/CustomStepper';
import HandleSelectedProperty from 'components/HandleSelectedProperty';
import { useStyles } from 'components/Styles';
import { useFormik } from 'formik';
import { useDependencyInjection } from 'hooks/useDependencyInjection';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { setNameProperty } from 'redux/nameProperty';
import { setNotIsFirstAccess } from 'redux/notIsFirstAccess';
import { setSelectedCycle } from 'redux/selectedCycle';
import { userPermissionsDomainAction } from 'redux/userPermissionsDomain/actions';
import { setCycleCompleted } from 'redux/userSteps';
import UserService from 'services/UserService';
import * as Yup from 'yup';
import Progress from '../../components/Progress';
import { FarmInfoOne } from './formSteps/farmInfo/farmInfo';
import UserInfo from './formSteps/userInfo/userInfo';
import WelcomeText from './formSteps/welcomeText/welcomeText';

function FirstProperty() {
  const [step, setStep] = useState(0);
  const [disableNextStep, setDisableNextStep] = useState(false);
  const matches = useMediaQuery('(max-width:900px)');
  const classes = useStyles();
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, handleState] = useState(null);
  const [selectedCity, handleCity] = useState(null);
  const [openDia, setOpenDia] = useState(false);
  const [openProgress, setOpenProgress] = useState(false);

  const [openHandleProperty, setOpenHandleProperty] = useState(false);
  const nameProperty = useSelector((state) => state.nameProperty.nameProperty);

  const {
    history,
    dispatch,
    loadingRequest,
    requestCompleted,
    api,
    success,
    error,
  } = useDependencyInjection();

  const handleStateSelect = async (state) => {
    formik.values.state = state.nome;
    formik.values.uf = state.sigla;
    handleState(state);
    formik.values.city = '';
    handleCity(null);
    const loadCities = await axios.get(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${state.sigla}/municipios`
    );
    setCities(loadCities.data);
  };

  const handleCitySelect = (city) => {
    formik.values.city = city.nome;
    handleCity(city);
  };

  useEffect(() => {
    async function loadProperty() {
      try {
        const response = await api.get(
          `/properties/${localStorage.getItem('idProperty')}`
        );
        const property = response.data;

        if (property === '') {
          setOpenDia(true);
        } else {
          formik.setValues(property);
          handleStateSelect({
            nome: property.state,
            sigla: property.uf || '',
          });
          handleCitySelect({
            nome: property.city,
          });
        }
      } catch (err) {
        console.error(err);
      }
    }

    async function loadStates() {
      try {
        setOpenProgress(true);
        const response = await axios.get(
          'https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome'
        );
        if (response.data.length !== 0) {
          setStates(response.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setOpenProgress(false);
      }
    }

    loadProperty();
    loadStates();
  }, [nameProperty]);

  const formik = useFormik({
    initialValues: {
      name: '',
      owner: '',
      phone: '',
      cnpj: '',
      cep: '',
      city: '',
      state: '',
      uf: '',
      street: '',
      number: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Campo Obrigatório'),
      owner: Yup.string()
        .required('Campo Obrigatório')
        .max(100, 'Máximo de caracteres ultrapassado (100 caracteres).')
        .nullable(),
      cnpj: Yup.string()
        .required('Campo Obrigatório')
        .matches(
          /^[0-9]{2}[.]?[0-9]{3}[.]?[0-9]{3}[/]?[0-9]{4}[-]?[0-9]{2}$/,
          'O número digitado não é válido.'
        ),
      cep: Yup.string()
        .required('Campo Obrigatório')
        .matches(/^[0-9]{5}[-]?[0-9]{3}$/, 'O número digitado não é válido.')
        .nullable(),
      street: Yup.string().required('Campo Obrigatório').nullable(),
      district: Yup.string().required('Campo Obrigatório').nullable(),
      number: Yup.number()
        .min(1, 'Número inválido')
        .integer('Número inválido')
        .transform((value) => (isNaN(value) ? undefined : value))
        .typeError(
          'O valor deve ser um número. Caso seja um número decimal utilize " . " ao invés de " , "'
        )
        .nullable(),
      city: Yup.string().required('Campo Obrigatório').nullable(),
      state: Yup.string().required('Campo Obrigatório').nullable(),
      uf: Yup.string().required('Campo Obrigatório').nullable(),
      phone: Yup.string()
        .matches(
          /^[(]?[0-9]{2}[)]?[\s]?[0-9]{5}[-]?[0-9]{4}$/,
          'O número digitado não é válido.'
        )
        .when('userTypeIsLegalPerson', {
          is: true,
          then: (schema) => schema.optional().nullable(),
          otherwise: (schema) => schema.required('Campo Obrigatório'),
        }),
    }),
    onSubmit: (values) => {
      request(values);
    },
  });

  async function request(data) {
    // envia os dados para api salvar no banco
    loadingRequest();
    setOpenProgress(true);

    try {
      const currentUser = await UserService.currentUser();

      const response = await api.post('/properties', { ...data });
      const { domain, access_token, domain_permissions, cycle_code } =
        response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('idProperty', domain.id);
      localStorage.setItem('nameProperty', domain.name);
      dispatch(setNameProperty(domain.name));
      dispatch(setSelectedCycle(cycle_code));
      dispatch(userPermissionsDomainAction(domain_permissions));

      if (currentUser.firstAccess) {
        await UserService.updateFirstAccess({ user: currentUser });
        dispatch(setNotIsFirstAccess(true));
        dispatch(setCycleCompleted(true));
      }
      success();
      history.push('/home');
    } catch (err) {
      console.log(err);
      error(err.response?.data.message);
    } finally {
      requestCompleted();
      setOpenProgress(false);
    }
  }

  const steps = ['Passo 1', 'Passo 2'];
  return (
    <Container style={{ padding: '3.5rem 8.125rem 3.5rem 8.9rem' }}>
      {!openHandleProperty && (
        <HandleSelectedProperty
          nameProperty={nameProperty}
          openHandleProperty={openHandleProperty}
          setOpenHandleProperty={setOpenHandleProperty}
        />
      )}
      {openProgress && <Progress />}
      <Card className={classes.roundedPaper}>
        <Grid container className={classes.centralized} xs={12}>
          <Grid
            item
            container
            className={classes.centralized}
            style={{ padding: '3rem 1rem' }}
          >
            {step === 0 ? <WelcomeText setStep={setStep} /> : null}

            {step > 0 && (
              <Grid
                item
                container
                xs={10}
                component="form"
                // className={`${classes.root} ${classes.centralized}`}
                onSubmit={formik.handleSubmit}
                style={{ padding: '0 1rem' }}
              >
                {step === 1 ? <UserInfo formik={formik} /> : null}
                {step === 2 ? (
                  <FarmInfoOne
                    formik={formik}
                    states={states}
                    handleStateSelect={handleStateSelect}
                    handleCitySelect={handleCitySelect}
                    selectedState={selectedState}
                    cities={cities}
                    selectedCity={selectedCity}
                  />
                ) : null}
                {/* {step === 3 ? (
                <FarmInfoTwo
                  formik={formik}
                  states={states}
                  handleStateSelect={handleStateSelect}
                  selectedState={selectedState}
                  cities={cities}
                  handleCitySelect={handleCitySelect}
                  selectedCity={selectedCity}
                />
              ) : null} */}
                {step > 0 ? (
                  <section
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <div>
                      <CustomStepper activeStep={step - 1}>
                        {steps.map((label) => (
                          <Step key={label}>
                            <StepLabel />
                          </Step>
                        ))}
                      </CustomStepper>
                    </div>
                    <div style={{ display: 'flex' }}>
                      {step > 1 ? (
                        <div style={{ marginInline: '-1rem' }}>
                          <Button
                            className={classes.roundedButton}
                            style={{
                              backgroundColor: '#fff',
                              border: '1px solid #00C051',
                              color: '#00C051',
                            }}
                            onClick={() => {
                              setStep((prev) => prev - 1);
                            }}
                            variant="text"
                          >
                            <Typography
                              style={{
                                textTransform: 'none',
                                paddingInline: '2rem',
                              }}
                            >
                              Voltar
                            </Typography>
                          </Button>
                        </div>
                      ) : null}
                      {step > 0 && step < 2 ? (
                        <Grid>
                          <Button
                            className="roundedButton"
                            onClick={async () => {
                              await formik.validateForm();
                              switch (step) {
                                case 1: {
                                  if (
                                    formik.touched.name &&
                                    !formik.errors.name &&
                                    formik.touched.cnpj &&
                                    !formik.errors.cnpj
                                  ) {
                                    setStep((prev) => prev + 1);
                                  }
                                  break;
                                }
                                case 2: {
                                  if (
                                    formik.values.cep &&
                                    formik.values.street &&
                                    formik.values.district &&
                                    formik.values.state &&
                                    formik.values.city &&
                                    formik.touched.phone &&
                                    !formik.errors.cep &&
                                    !formik.errors.street &&
                                    !formik.errors.district &&
                                    !formik.errors.state &&
                                    !formik.errors.city &&
                                    !formik.errors.phone
                                  ) {
                                    setStep((prev) => prev + 1);
                                  }
                                  break;
                                }
                                // case 3: {
                                //   if (
                                //     formik.values.state
                                //         && formik.values.city
                                //         && formik.touched.phone
                                //         && !formik.errors.state
                                //         && !formik.errors.city
                                //         && !formik.errors.phone
                                //   ) {
                                //     setStep((prev) => prev + 1);
                                //   }
                                //   break;
                                // }
                                default:
                                  return setDisableNextStep(true);
                              }
                            }}
                            variant="contained"
                            style={{
                              backgroundColor: disableNextStep
                                ? 'gray'
                                : undefined,
                            }}
                          >
                            <Typography
                              style={{
                                textTransform: 'none',
                                paddingInline: '0rem',
                              }}
                            >
                              {!matches ? 'Próximo' : <ArrowForwardIcon />}
                            </Typography>
                          </Button>
                        </Grid>
                      ) : (
                        <Grid>
                          <Button
                            className="roundedButton"
                            disabled={!formik.isValid}
                            type={formik.isValid ? 'submit' : 'button'}
                            variant="contained"
                            style={{
                              backgroundColor: !formik.isValid
                                ? 'gray'
                                : undefined,
                            }}
                          >
                            <Typography
                              style={{
                                textTransform: 'none',
                                paddingInline: '0rem',
                              }}
                            >
                              Concluir
                            </Typography>
                          </Button>
                        </Grid>
                      )}
                    </div>
                  </section>
                ) : null}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Card>
    </Container>
  );
}

export default FirstProperty;
